import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { Avatar, Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { TopManagerLeaderDto } from '@sr/dto';
import { topManagerApi } from 'entities/reports/top-manager/top-manager.api';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { ErrorBanner } from 'shared/ui/error-banner';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { formatDateMonthYear } from 'utils/date-format-helpers';

const podiumPlaces = [
  { color: '#d4af37', height: 88, column: 2 },
  { color: '#526277', height: 64, column: 1 },
  { color: '#945536', height: 48, column: 3 }
];

export const TopManagers = () => {
  const { currentData, isFetching, error } = topManagerApi.useGetTopManagerLeadersQuery(undefined, {
    refetchOnMountOrArgChange: 60
  });
  const now = new Date();
  const managers = currentData ?? [];
  const errorMessage = extractNestJsErrorMessage(error);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, height: '100%', minWidth: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <EmojiEventsOutlinedIcon sx={{ color: podiumPlaces[0].color }} />
            <Typography variant="h6">Лидеры месяца</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            По чистой прибыли · {formatDateMonthYear(now.getFullYear(), now.getMonth())}
          </Typography>
        </Box>
      </Stack>
      <Box sx={{ flexGrow: 1 }}>
        {errorMessage ? (
          <Box sx={{ mt: 2 }}><ErrorBanner errorMessage={errorMessage} /></Box>
        ) : isFetching && !currentData ? (
          <Box sx={{ mt: 3 }}><LoadingBanner /></Box>
        ) : !managers.length ? (
          <Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
            В этом месяце пока нет лидеров по чистой прибыли
          </Typography>
        ) : (
          <>
            <Box component="ol" aria-label="Лидеры месяца" sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', alignItems: 'end', gap: { xs: 1, sm: 2 }, listStyle: 'none', p: 0, mt: 3, mb: 0 }}>
              {managers.slice(0, 3).map((manager, index) => <PodiumPlace key={index} manager={manager} index={index} />)}
            </Box>
            {managers.length > 3 && (
              <Stack component="ol" start={4} spacing={1} sx={{ listStyle: 'none', p: 0, mb: 0, mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                {managers.slice(3).map((manager, index) => (
                  <Stack component="li" key={index} direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 20 }}>{index + 4}</Typography>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{manager.fullName}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

const PodiumPlace = ({ manager, index }: { manager: TopManagerLeaderDto; index: number }) => {
  const { color, height, column } = podiumPlaces[index];
  const textColor = index === 0 ? '#785600' : color;
  const initials = manager.fullName.trim().split(/\s+/).slice(0, 2).map((name) => name[0]).join('');

  return (
    <Box component="li" aria-label={`${index + 1} место: ${manager.fullName}`} sx={{ gridColumn: column, gridRow: 1, minWidth: 0, textAlign: 'center' }}>
      <Avatar sx={{ mx: 'auto', mb: 1, width: index === 0 ? 48 : 40, height: index === 0 ? 48 : 40, bgcolor: alpha(color, 0.12), color: textColor, fontSize: 16, fontWeight: 600, border: '2px solid', borderColor: alpha(color, 0.3) }}>
        {initials}
      </Avatar>
      <Typography variant="body2" sx={{ minHeight: 40, mb: 1.5, fontWeight: 600, overflowWrap: 'anywhere' }}>
        {manager.fullName}
      </Typography>
      <Box sx={{ height, borderRadius: '8px 8px 0 0', background: index === 0 ? 'linear-gradient(180deg, #ffe8a3, #e6bd4d)' : `linear-gradient(180deg, ${alpha(color, 0.19)}, ${alpha(color, 0.05)})`, borderTop: '3px solid', borderColor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        <Typography component="span" sx={{ fontSize: index === 0 ? 34 : 28, fontWeight: 700, color: textColor }}>{index + 1}</Typography>
        <Typography variant="caption" sx={{ color: textColor }}>место</Typography>
      </Box>
    </Box>
  );
};
